import { RecentsList } from "@/store/pages/SearchPageStore/QuickSearchModal/RecentsList";
import { css, cx } from "@/domains/emotion";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { motion, AnimatePresence, MotionProps } from "motion/react";
import { useEffect } from "react";
import { SuggestedSearchesList } from "@/store/pages/SearchPageStore/QuickSearchModal/SuggestedSearchesList";
import { QuickSearchModalSearchBar } from "@/store/pages/SearchPageStore/QuickSearchModal/QuickSearchModalSearchBar";
import { QuickSearchModalNotePreview } from "@/store/pages/SearchPageStore/QuickSearchModal/QuickSearchModalNotePreview";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { AppearAnimator, MDSIconButton, MDSIconKind } from "@/mds";
import styled from "@emotion/styled";
import { Modal } from "react-aria-components";
import { ZIndex } from "@/domains/design/constants";

const initialModelStyles: MotionProps["initial"] = {
  backdropFilter: "blur(6.0px) grayscale(70%) contrast(80%)",
};

const animateModalStyles: MotionProps["animate"] = {
  backdropFilter: "unset",
};

const initialContentStyles: MotionProps["initial"] = {
  transform: "scale(0.98) translateY(8px)",
};

const animateContentStyles: MotionProps["initial"] = {
  transform: "scale(1) translateY(0)",
};

export const QuickSearchModal = observer(function QuickSearchModal() {
  const { pageStore } = useAppStore();
  const state = pageStore.quickSearchModal;
  state.listState.useListStateEffects();

  const combinedScrimStyles = cx(scrimStyles);

  /**
   * Track views of the quick search modal.
   */
  useEffect(() => {
    if (state.isOpen) {
      trackEvent(TrackedEvent.QuickSearchView);
    }
  }, [state.isOpen]);

  return (
    <AnimatePresence>
      {state.isOpen && (
        <StyledMDSModal
          isOpen={state.isOpen}
          onOpenChange={open => {
            if (!open) state.close();
          }}
        >
          <motion.div
            className={combinedScrimStyles}
            onKeyDown={state.handleKeyDown}
            onClick={state.close}
            initial={{ ...initialModelStyles, opacity: 0 }}
            animate={{ ...animateModalStyles, opacity: 1 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <CloseButtonContainer>
              <MDSIconButton onClick={state.close} iconKind={MDSIconKind.Exit} />
            </CloseButtonContainer>
            <AnimatePresence>
              <Container
                initial={{ ...initialContentStyles }}
                animate={{ ...animateContentStyles }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                <QuickSearchModalSearchBar />
                <Center>
                  <StyledColumns>
                    {state.shouldShowRecentsSection && (
                      <StyledContainer visible={true}>
                        <RecentsList />
                      </StyledContainer>
                    )}
                    {state.shouldShowSuggestionsSection && (
                      <StyledContainer visible={true}>
                        <SuggestedSearchesList />
                      </StyledContainer>
                    )}
                    {state.selectedNoteObservable && (
                      <QuickSearchModalNotePreview
                        key={state.selectedNoteObservable.id}
                        noteObservable={state.selectedNoteObservable}
                      />
                    )}
                  </StyledColumns>
                </Center>
              </Container>
            </AnimatePresence>
          </motion.div>
        </StyledMDSModal>
      )}
    </AnimatePresence>
  );
});

const StyledMDSModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.surface.pane};
  background: ${({ theme }) => theme.colors.surface.pane};
  border: none;
`;

const scrimStyles = css({
  alignItems: "center",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  justifyContent: "center",
  position: "relative",
  width: "100%",
});

const CloseButtonContainer = styled.div`
  position: absolute;
  z-index: ${ZIndex.QuickSearchModel};
  right: ${({ theme }) => theme.spacing["4"]};
  top: ${({ theme }) => theme.spacing["4"]};
`;

const Container = styled(motion.div)`
  min-height: 100%;
  width: 100%;
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  /* Constrain height for proper scrolling, subtracting the search bar height */
  max-height: calc(100vh - 154px);
  width: 100%;
  align-items: center;
  overflow: hidden; /* Enable scrolling on child components */
`;

const StyledColumns = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing["5"]};
  padding-top: ${({ theme }) => theme.spacing["8"]};
  height: 100%;
  max-width: 920px;
  width: 100%;
  /* Ensure we don't exceed viewport */
  max-height: calc(100vh - 154px);
`;

const StyledContainer = styled(AppearAnimator)`
  position: relative;
  height: 100%;
  /* Constrain height for scrolling */
  max-height: calc(100vh - 154px);
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden; /* Enable scrolling on child components */

  /* Ensure the virtualized list can scroll properly */
  > div {
    height: 100%;
    overflow-y: auto;
    -ms-overflow-style: none;
  }
`;
